/**
 * Redux actions for data
 * @module services/data/redux/actions
 */
import { Map, fromJS } from 'immutable'

export const RESET_DATA = 'RESET_DATA'
export const DELETE_DATA_APPLICATION = 'DELETE_DATA_APPLICATION'
export const SET_DATA_APPLICATION = 'SET_DATA_APPLICATION'
export const DELETE_DATA_APPLICATION_BY_KEY = 'DELETE_DATA_APPLICATION_BY_KEY'
export const SET_DATA_APPLICATION_BY_KEY = 'SET_DATA_APPLICATION_BY_KEY'
export const GET_DATA_LIST = 'GET_DATA_LIST'
export const SET_DATA_LIST = 'SET_DATA_LIST'
export const MUTATE_DATA = 'MUTATE_DATA'
export const PERSIST_DATA = 'PERSIST_DATA'

/**
 * Reset a store key back to a default state
 * @param {Object} params The paramters
 * @param {String} params.storeKey The store key in redux
 * @returns A redux action
 */
export function resetData (params = {}) {
  const { storeKey } = params
  if (!storeKey) {
    throw new Error('The storeKey parameter is required')
  }
  return {
    type: RESET_DATA,
    payload: { storeKey },
  }
}

/**
 * Set the application state under the store key
 * @param {Object} params The paramters
 * @param {String} params.storeKey The store key in redux
 * @param {Map} params.application The application store under storeKey.application
 * @returns A redux action
 */
export function setDataApplication (params = {}) {
  const { storeKey, application } = params
  if (!storeKey) {
    throw new Error('The storeKey parameter is required')
  }
  if (!Map.isMap(application)) {
    throw new Error('The application parameter is required to be a Map')
  }
  return {
    type: SET_DATA_APPLICATION,
    payload: { storeKey, application },
  }
}

/**
 * Delete the application state under the store key
 * @param {Object} params The paramters
 * @param {String} params.storeKey The store key in redux
 * @param {Map} params.application The application store under storeKey.application
 * @returns A redux action
 */
export function deleteDataApplication (params = {}) {
  const { storeKey } = params
  if (!storeKey) {
    throw new Error('The storeKey parameter is required')
  }
  return {
    type: DELETE_DATA_APPLICATION,
    payload: { storeKey },
  }
}

/**
 * Set the application state using the application key under the store key
 * @param {Object} params The paramters
 * @param {String} params.storeKey The store key in redux
 * @param {Map} params.applicationData The application store under storeKey.application
 * @param {String} params.applicationKey The application key to merge the applicationData
 * @returns A redux action
 */
export function setDataApplicationByKey (params = {}) {
  const { storeKey, applicationData, applicationKey } = params
  if (!storeKey) {
    throw new Error('The storeKey parameter is required')
  }
  if (!applicationKey) {
    throw new Error('The applicationKey parameter is required')
  }
  if (!Map.isMap(applicationData)) {
    throw new Error('The applicationData parameter is required to be a Map')
  }
  return {
    type: SET_DATA_APPLICATION_BY_KEY,
    payload: { storeKey, applicationData, applicationKey },
  }
}

/**
 * Delete the application state using the application key under the store key
 * @param {Object} params The paramters
 * @param {String} params.storeKey The store key in redux
 * @param {String} params.applicationKey The application key to merge the applicationData
 * @returns A redux action
 */
export function deleteDataApplicationByKey (params = {}) {
  const { storeKey, applicationKey } = params
  if (!storeKey) {
    throw new Error('The storeKey parameter is required')
  }
  if (!applicationKey) {
    throw new Error('The applicationKey parameter is required')
  }
  return {
    type: DELETE_DATA_APPLICATION_BY_KEY,
    payload: { storeKey, applicationKey },
  }
}

/**
 * Set a list of data
 * @param {Object} params The paramters
 * @param {String} params.storeKey The store key in redux
 * @param {Boolean} [params.processing=true] The processing state of the data
 * @param {Map} [params.records] The records under storeKey.records
 * @param {Map} [params.application] The application store under storeKey.application
 * @param {Number} [params.total] The total items in the data
 * @param {Error} [params.error] An error if one was encounter while fethcing the list
 * @param {Map|Object} [params.options] The view options also used for the fetch request
 * @returns A redux action
 */
export function getDataList (params = {}) {
  const {
    storeKey, processing = true, options, records, application, total, error,
  } = params
  if (!storeKey) {
    throw new Error('The storeKey parameter is required')
  }
  if (records && !Map.isMap(records)) {
    throw new Error('The records parameter is required to be a Map')
  }
  if (application && !Map.isMap(application)) {
    throw new Error('The application parameter is required to be a Map')
  }
  return {
    type: GET_DATA_LIST,
    payload: {
      storeKey, records, application, options: fromJS(options), total, processing, error,
    },
  }
}

/**
 * Set a list of data
 * @param {Object} params The paramters
 * @param {String} params.storeKey The store key in redux
 * @param {Boolean} [params.processing=false] The processing state of the data
 * @param {Map} [params.records] The records under storeKey.records
 * @param {Map} [params.application] The application store under storeKey.application
 * @param {Number} [params.total] The total items in the data
 * @param {Error} [params.error] An error if one was encounter while fethcing the list
 * @param {Map|Object} [params.options] The view options also used for the fetch request
 * @returns A redux action
 */
export function setDataList (params = {}) {
  const {
    storeKey, processing = false, options = {}, records, application, total, error,
  } = params
  if (!storeKey) {
    throw new Error('The storeKey parameter is required')
  }
  if (records && !Map.isMap(records)) {
    throw new Error('The records parameter is required to be a Map')
  }
  if (application && !Map.isMap(application)) {
    throw new Error('The application parameter is required to be a Map')
  }
  return {
    type: SET_DATA_LIST,
    payload: {
      storeKey, records, application, options: fromJS(options), total, processing, error,
    },
  }
}

/**
 * Write data to the database
 * @param {Object} params The parameters
 * @param {String} params.storeKey The store key in redux
 * @param {String} params.applicationKey The property on the data to use as a key for the item
 * @returns A redux action
 */
export function mutateData (params = {}) {
  const { storeKey, applicationKey } = params
  if (!storeKey) {
    throw new Error('The storeKey parameter is required')
  }
  if (!applicationKey) {
    throw new Error('The applicationKey parameter is required')
  }
  return {
    type: MUTATE_DATA,
    payload: { storeKey, applicationKey },
  }
}

/**
 * Persist data to the backend
 * @param {Object} params The parameters
 * @param {String} params.storeKey The store key in redux
 * @param {Boolean} [params.processing=true] The processing state of the data
 * @param {Map} params.record The record matching the schema in storeKey.records to persiste
 * @param {Map|Object} params.options The view options also used for the api request
 * @returns A redux action
 */
export function persistData (params = {}) {
  const {
    storeKey, processing = false, options = {}, record, applicationKey,
  } = params
  if (!storeKey) {
    throw new Error('The storeKey parameter is required')
  }
  if (!record) {
    throw new Error('The record parameter is required')
  }
  return {
    type: PERSIST_DATA,
    payload: {
      storeKey, record, options: fromJS(options), processing, applicationKey,
    },
  }
}
